import { Fragment, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useParams } from 'react-router-dom';
import {
  useDeleteGiftcardMutation,
  useGiftcardsList,
} from '../../../stores/FetcherStores/GiftcardFetcher';
import LoadingWrapper from '../../../components/LoadingWrapper';
import { Money } from '../../../utils/currency';
import EditGiftcard from '../../../components/EditGiftcard';
import { useAlertStore } from '../../../stores';

const Giftcards = observer(() => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [editGiftcardOpen, setEditGiftcardOpen] = useState<boolean>(false);
  const [selectedGiftcardId, setSelectedGiftcardId] = useState<string>('');
  const params = useParams() as { companyKey: string };
  const companyId = params.companyKey;
  const alertStore = useAlertStore();
  const { t } = useTranslation();

  const { data: giftcards, isLoading } = useGiftcardsList(companyId);
  const { mutate: deleteGiftcard, isLoading: isDeleteLoading } = useDeleteGiftcardMutation();

  const handleOpenDeleteDialog = (giftcardId: string) => {
    if (!giftcardId) return;

    setSelectedGiftcardId(giftcardId);
    setDeleteDialogOpen(true);
  };

  const handleOpenEdit = (giftcardId: string) => {
    if (!giftcardId) return;

    setSelectedGiftcardId(giftcardId);
    setEditGiftcardOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedGiftcardId('');
    setDeleteDialogOpen(false);
  };

  if (!giftcards) {
    return null;
  }

  return (
    <Box>
      <Box sx={{ marginTop: 2, marginBottom: 2 }}>
        <Alert severity="warning">
          Warning! Ändringar av belopp samt tillägg/borttagning av presentkort kommer inte bokföras
          av Enklakassan.
        </Alert>
      </Box>
      <Typography variant="h1" gutterBottom>
        Presentkort
      </Typography>
      <Paper sx={{ marginTop: 2, marginBottom: 2, padding: '1rem' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'end' }}>
          <Button
            variant="contained"
            disabled={isLoading}
            onClick={() => {
              setEditGiftcardOpen(true);
              setSelectedGiftcardId('');
            }}
          >
            Lägg till presentkort
          </Button>
        </Box>
        <LoadingWrapper loading={isLoading}>
          <TableContainer sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Titel</TableCell>
                  <TableCell>Kortnummer</TableCell>
                  <TableCell>Giltigt till</TableCell>
                  <TableCell align="right">Tillgängligt belopp</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {giftcards.map((giftcard) => (
                  <Fragment key={giftcard.id}>
                    <TableRow sx={{ '& > td': { borderBottom: 'none' }, p: 0 }}>
                      <TableCell>{giftcard.title}</TableCell>
                      <TableCell>{giftcard.cardNumber}</TableCell>
                      <TableCell>{giftcard.expiryDate}</TableCell>
                      <TableCell align="right">{new Money(giftcard.amount).format()}</TableCell>
                      <TableCell align="right">
                        <Box>
                          <IconButton onClick={() => handleOpenEdit(giftcard.id)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleOpenDeleteDialog(giftcard.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </LoadingWrapper>
      </Paper>
      {editGiftcardOpen && (
        <EditGiftcard
          giftcardId={selectedGiftcardId}
          open={editGiftcardOpen}
          onClose={() => setEditGiftcardOpen(false)}
        />
      )}
      <Dialog
        open={!!selectedGiftcardId && deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Vill du verkligen ta bort presentkortet?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={isDeleteLoading}
            onClick={() =>
              deleteGiftcard(companyId, selectedGiftcardId).then(() => {
                alertStore.pushAlert({
                  message: 'Presentkortet har tagits bort',
                  severity: 'success',
                });
                handleCloseDeleteDialog();
              })
            }
            color="error"
            variant="contained"
          >
            {t('productScreen.yes')}
          </Button>
          <Button onClick={handleCloseDeleteDialog} variant="contained" autoFocus>
            {t('productScreen.no')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default Giftcards;
